import type { BaseDocument, Ref } from "./data";
import type { Installation } from "./installation";
import type { Sensor } from "./sensor";
import type { Zone } from "./zone";

/**
 * Category of sensor data being ingested. Drives downstream normalization
 * once that pipeline is built. New categories append here.
 */
export enum SensorDataType {
	AiDantEvent = "ai_dant_event",
	AirQuality = "air_quality",
	AuditLog = "audit_log",
	Button = "button",
	Contact = "contact",
	Lock = "lock",
	Motion = "motion",
	ObjectAnalytics = "object_analytics",
	SoundPressure = "sound_pressure",
}

/**
 * Format of an on-prem sensor payload. New formats append here as they are
 * integrated.
 */
export enum SensorFormat {
	Axis = "axis",
	HomeAssistant = "homeassistant",
}

/**
 * Input parameters for recording a raw sensor data document. IDs are internal
 * Mongo refs (resolved from external IDs by the service layer).
 */
export interface CreateRawSensorDataParams {
	capturedAt?: Date;
	format: SensorFormat;
	installation: string;
	payload: unknown;
	sensor: string;
	sensorType: SensorDataType;
	videoSensorId?: string;
	zone?: string;
}

/**
 * Raw sensor data document. The opaque vendor payload is stored verbatim in
 * `payload`; the wrapper fields exist so downstream processing can route by
 * format/type without parsing the payload.
 */
export interface RawSensorData extends BaseDocument {
	/** Optional timestamp the device claims the reading was captured. */
	capturedAt?: Date;
	/** Format of the source payload. */
	format: SensorFormat;
	/** Installation that owns the sensor that produced this data. */
	installation: Ref<Installation>;
	/** Verbatim vendor payload. */
	payload: unknown;
	/** Sensor that produced this data. */
	sensor: Ref<Sensor>;
	/** Category of sensor data. */
	sensorType: SensorDataType;
	/**
	 * External id of the co-located video sensor this metadata stream narrates,
	 * as declared by the edge. Kept as the raw external string — the video
	 * sensor may not be provisioned yet when the fact arrives.
	 */
	videoSensorId?: string;
	/** Zone the sensor belongs to, when known. */
	zone?: Ref<Zone>;
}
